import { useTranslation } from 'react-i18next'
import { useDateFormat } from '../i18n/useDateFormat'
import { cx } from '../lib/cx'
import { formatKwacha, kwachaForScreenReader } from '../lib/money'
import './AmountDisplay.css'

export type AmountTone = 'neutral' | 'positive' | 'owed'

export interface AmountTextProps {
  /** Integer ngwee: K1,234.50 is 123450. */
  valueNgwee: number
  /**
   * neutral: as is ("-K12.00" when negative). positive: money coming in, "+K12.00".
   * owed: money she owes, "-K12.00 owed" whatever the sign; never colour alone.
   */
  tone?: AmountTone
  className?: string
}

/**
 * An amount inline, in tabular figures. Sighted readers see "K1,234.50"; screen
 * readers hear "1,234 kwacha 50 ngwee" (PRD section 17).
 */
export function AmountText({ valueNgwee, tone = 'neutral', className }: AmountTextProps) {
  const { t } = useTranslation()
  const magnitude = Math.abs(valueNgwee)
  // "kwacha" and "ngwee" are never translated; the words around them are.
  const words = kwachaForScreenReader(magnitude)
  // The sign and figure never part ("-" alone on a line above "K5,750.00"); only a word after them may wrap.
  let figure: string
  let suffix = ''
  let spoken: string
  if (tone === 'owed') {
    figure = `-${formatKwacha(magnitude)}`
    suffix = t('money.owed')
    spoken = t('money.srOwed', { amount: words })
  } else if (tone === 'positive' && valueNgwee > 0) {
    figure = `+${formatKwacha(valueNgwee)}`
    spoken = t('money.srPlus', { amount: words })
  } else {
    figure = formatKwacha(valueNgwee)
    spoken = valueNgwee < 0 ? t('money.srMinus', { amount: words }) : words
  }
  return (
    <span className={cx('amount', `amount--${tone}`, className)} data-amount={valueNgwee}>
      <span aria-hidden="true">
        <span className="amount__figure">{figure}</span>
        {suffix && ` ${suffix}`}
      </span>
      <span className="visually-hidden">{spoken}</span>
    </span>
  )
}

export interface AmountDisplayProps extends AmountTextProps {
  /** lg: type-display (headline balance); md: type-heading; sm: type-body. Default md. */
  size?: 'lg' | 'md' | 'sm'
  /** What the amount is, shown above it: "Total savings". */
  label?: string
  /** When the figure was true: "As of 16 Oct 2026, 08:42". */
  asOf?: Date
}

/** A labelled amount: the PRD AmountDisplay. Numbers first, tabular figures, never truncated. */
export function AmountDisplay({ valueNgwee, size = 'md', tone = 'neutral', label, asOf, className }: AmountDisplayProps) {
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormat()
  return (
    <div className={cx('amount-display', `amount-display--${size}`, className)}>
      {label && <span className="amount-display__label">{label}</span>}
      <AmountText valueNgwee={valueNgwee} tone={tone} className="amount-display__value" />
      {asOf && <span className="amount-display__as-of">{t('money.asOf', { date: formatDateTime(asOf) })}</span>}
    </div>
  )
}
